/**
 * Strategy Lab custom rules — the sentence builder's model (rulelang, BE `rulelang/`).
 *
 * The builder covers the common shapes: one price (optionally a distance away from it,
 * or a level inside a zone), two prices combined (min / max / further / nearer), and a
 * condition list joined by and / or. Anything else stays editable as JSON: `…FromJson`
 * returns null and the editor opens in JSON mode. The BE checks and reads back every
 * rule (`POST /rules/compile`); nothing here evaluates one.
 */
import type {
  StrategyRdEntryMode,
  StrategyRdManageRule,
  StrategyRdRuleExpr,
  StrategyRdRuleRole,
  StrategyRdRuleTemplate,
  StrategyRdRuleVocabulary,
  StrategyRdSidePolicy,
} from "../types/strategyRd";

export type StrategyRdRuleDistanceUnit = "atr" | "pips" | "ticks" | "pct" | "r";
export type StrategyRdRuleOffsetDir = "beyond" | "toward" | "plus" | "minus";
export type StrategyRdRuleCombineOp = "min" | "max" | "further" | "nearer";

/** `{ref: {of, value}}` — `value: "zone"` with `levelPct` is `level_in(zone, pct)`. */
export interface StrategyRdPriceForm {
  of: string;
  value: string;
  /** 0–1: a level inside the zone, from the edge price reaches first. */
  levelPct?: number | null;
  offset?: { dir: StrategyRdRuleOffsetDir; unit: StrategyRdRuleDistanceUnit; amount: number } | null;
}

export interface StrategyRdPriceSentence {
  combine?: StrategyRdRuleCombineOp | null;
  /** 1 part, or 2 with `combine`. */
  parts: StrategyRdPriceForm[];
}

export type StrategyRdPriceTestOp =
  | "closes_past"
  | "closes_back"
  | "close_above"
  | "close_below"
  | "touches"
  | "trades_past";
export type StrategyRdCompareOp = "gte" | "gt" | "lte" | "lt";

export type StrategyRdRuleOperand =
  | { k: "height"; of: string }
  | { k: "span"; a: { of: string; value: string }; b: { of: string; value: string } }
  | { k: "dist"; unit: StrategyRdRuleDistanceUnit; amount: number }
  | { k: "price"; form: StrategyRdPriceForm };

export type StrategyRdCondTest =
  | { t: "price"; op: StrategyRdPriceTestOp; price: StrategyRdPriceForm }
  | { t: "time"; from: string; to: string; tz: string }
  | { t: "compare"; op: StrategyRdCompareOp; left: StrategyRdRuleOperand; right: StrategyRdRuleOperand }
  | { t: "overlaps"; a: string; b: string }
  | { t: "inside"; price: StrategyRdPriceForm; zone: string };

export interface StrategyRdCondItem {
  negate?: boolean;
  /** `consecutive` = n bars in a row; `count` = n bars since the setup (not in filters). */
  repeat?: { kind: "consecutive" | "count"; n: number } | null;
  test: StrategyRdCondTest;
}

export interface StrategyRdCondSentence {
  join: "and" | "or";
  items: StrategyRdCondItem[];
}

export const STRATEGY_RD_RULE_DIST_UNITS: readonly StrategyRdRuleDistanceUnit[] = [
  "atr",
  "pips",
  "ticks",
  "pct",
  "r",
];
const OFFSET_DIRS: readonly StrategyRdRuleOffsetDir[] = ["beyond", "toward", "plus", "minus"];
const COMBINE_OPS: readonly StrategyRdRuleCombineOp[] = ["min", "max", "further", "nearer"];
export const STRATEGY_RD_PRICE_TESTS: readonly StrategyRdPriceTestOp[] = [
  "closes_past",
  "closes_back",
  "close_above",
  "close_below",
  "touches",
  "trades_past",
];
export const STRATEGY_RD_COMPARE_OPS: readonly StrategyRdCompareOp[] = ["gte", "gt", "lte", "lt"];
/** BE `cond._TZ`. */
export const STRATEGY_RD_RULE_TIME_ZONES = [
  "UTC",
  "Europe/London",
  "America/New_York",
  "Asia/Tokyo",
  "Europe/Berlin",
  "Asia/Hong_Kong",
  "Australia/Sydney",
] as const;

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

const SOURCE_LABELS: Record<string, string> = {
  signal: "Signal candle",
  setup: "Setup",
  entry: "Entry",
  prior_day: "Prior day",
  prior_week: "Prior week",
  prior_month: "Prior month",
  swing: "Last swing",
};

const VALUE_LABELS: Record<string, string> = {
  low: "low",
  high: "high",
  mid: "middle",
  open: "open",
  close: "close",
  far_edge: "far edge",
  near_edge: "near edge",
  zone: "zone",
  level: "level",
  price: "price",
};

export const STRATEGY_RD_OFFSET_DIR_LABELS: Record<StrategyRdRuleOffsetDir, string> = {
  beyond: "beyond",
  toward: "past, with the trade,",
  plus: "above",
  minus: "below",
};

export const STRATEGY_RD_COMBINE_LABELS: Record<StrategyRdRuleCombineOp, string> = {
  min: "the lowest of",
  max: "the highest of",
  further: "whichever is further from the entry of",
  nearer: "whichever is nearer the entry of",
};

export const STRATEGY_RD_DIST_UNIT_LABELS: Record<StrategyRdRuleDistanceUnit, string> = {
  atr: "× ATR",
  pips: "pips",
  ticks: "ticks",
  pct: "% of price",
  r: "R",
};

export const STRATEGY_RD_PRICE_TEST_LABELS: Record<StrategyRdPriceTestOp, string> = {
  closes_past: "closes past",
  closes_back: "closes back through",
  close_above: "closes above",
  close_below: "closes below",
  touches: "touches",
  trades_past: "trades past",
};

export const STRATEGY_RD_COMPARE_LABELS: Record<StrategyRdCompareOp, string> = {
  gte: "is at least",
  gt: "is more than",
  lte: "is at most",
  lt: "is less than",
};

export function strategyRdRuleSourceLabel(
  key: string,
  vocabulary?: StrategyRdRuleVocabulary | null
): string {
  const slot = vocabulary?.sources[key];
  if (slot && !Array.isArray(slot) && slot.name) return slot.name;
  return SOURCE_LABELS[key] || key.replace(/_/g, " ");
}

export function strategyRdRuleValueLabel(value: string): string {
  return VALUE_LABELS[value] || value.replace(/_/g, " ");
}

// ---------------------------------------------------------------------------
// Vocabulary helpers
// ---------------------------------------------------------------------------

/** Values a source offers (`sources[key]` is a list for built-ins, `{values}` for slots). */
export function strategyRdRuleSourceValues(
  vocabulary: StrategyRdRuleVocabulary | null | undefined,
  key: string
): string[] {
  const src = vocabulary?.sources[key];
  if (!src) return [];
  return Array.isArray(src) ? src : src.values;
}

/** Confluence slot keys (not the built-in sources). */
export function strategyRdRuleSlotKeys(vocabulary: StrategyRdRuleVocabulary | null | undefined): string[] {
  return Object.entries(vocabulary?.sources ?? {})
    .filter(([, v]) => !Array.isArray(v))
    .map(([k]) => k);
}

/**
 * Sources a role may read. In entry rules and filters there is no entry yet (`entry` is
 * rejected by the BE), so it is not offered there.
 */
export function strategyRdRuleSourcesForRole(
  vocabulary: StrategyRdRuleVocabulary | null | undefined,
  role: StrategyRdRuleRole
): string[] {
  const keys = Object.keys(vocabulary?.sources ?? {});
  return role === "entry" || role === "filter" ? keys.filter((k) => k !== "entry") : keys;
}

/** Price values (not `zone`) of a source for this role (`signal.open`: filters / entry only). */
export function strategyRdRulePriceValues(
  vocabulary: StrategyRdRuleVocabulary | null | undefined,
  of: string,
  role: StrategyRdRuleRole
): string[] {
  return strategyRdRuleSourceValues(vocabulary, of).filter(
    (v) => !(of === "signal" && v === "open" && (role === "stop" || role === "target"))
  );
}

export function strategyRdRuleZoneSources(
  vocabulary: StrategyRdRuleVocabulary | null | undefined,
  role: StrategyRdRuleRole
): string[] {
  return strategyRdRuleSourcesForRole(vocabulary, role).filter((k) =>
    strategyRdRuleSourceValues(vocabulary, k).includes("zone")
  );
}

/** Distance units for a role: `r` (× risk) only exists once there is an entry — targets. */
export function strategyRdRuleDistUnits(
  vocabulary: StrategyRdRuleVocabulary | null | undefined,
  role: StrategyRdRuleRole
): StrategyRdRuleDistanceUnit[] {
  const listed = Object.keys(vocabulary?.distances ?? {});
  const base = listed.length
    ? STRATEGY_RD_RULE_DIST_UNITS.filter((u) => listed.includes(u))
    : [...STRATEGY_RD_RULE_DIST_UNITS];
  return role === "target" ? base : base.filter((u) => u !== "r");
}

/** `vocabulary.examples` / `entry.examples` / `filter.examples` for one role. */
export function strategyRdRuleExamples(
  vocabulary: StrategyRdRuleVocabulary | null | undefined,
  role: StrategyRdRuleRole
): Array<{ label: string; rule: StrategyRdRuleExpr }> {
  if (!vocabulary) return [];
  const pick = (map: Record<string, StrategyRdRuleExpr> | undefined | null) =>
    Object.entries(map ?? {}).map(([label, rule]) => ({ label, rule }));
  if (role === "entry") return pick(vocabulary.entry?.examples);
  if (role === "filter") return pick(vocabulary.filter?.examples);
  return pick(vocabulary.examples).filter(({ label }) =>
    label.trim().toLowerCase().startsWith(role)
  );
}

/** Rule refs (`of`) that point at slot keys this recipe no longer has. */
export function strategyRdRuleUnknownSources(
  rule: unknown,
  vocabulary: StrategyRdRuleVocabulary | null | undefined
): string[] {
  if (!vocabulary) return [];
  const known = new Set(Object.keys(vocabulary.sources));
  const out = new Set<string>();
  const walk = (n: unknown): void => {
    if (Array.isArray(n)) {
      n.forEach(walk);
      return;
    }
    if (!n || typeof n !== "object") return;
    for (const [op, arg] of Object.entries(n as Record<string, unknown>)) {
      if (op === "ref" && arg && typeof arg === "object") {
        const of = String((arg as Record<string, unknown>).of ?? "");
        if (of && !known.has(of)) out.add(of);
      } else walk(arg);
    }
  };
  walk(rule);
  return Array.from(out);
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export function strategyRdDefaultPriceForm(
  vocabulary: StrategyRdRuleVocabulary | null | undefined,
  role: StrategyRdRuleRole
): StrategyRdPriceForm {
  if (role === "target") {
    return { of: "entry", value: "price", offset: { dir: "toward", unit: "r", amount: 2 } };
  }
  const sources = strategyRdRuleSourcesForRole(vocabulary, role);
  const of = sources.includes("signal") ? "signal" : sources[0] || "signal";
  const values = strategyRdRulePriceValues(vocabulary, of, role);
  const value = values.includes("far_edge") ? "far_edge" : values.find((v) => v !== "zone") || "far_edge";
  return role === "stop"
    ? { of, value, offset: { dir: "beyond", unit: "atr", amount: 0.1 } }
    : { of, value };
}

export function strategyRdDefaultCondItem(
  vocabulary: StrategyRdRuleVocabulary | null | undefined,
  role: StrategyRdRuleRole
): StrategyRdCondItem {
  if (role === "filter") {
    const zones = strategyRdRuleZoneSources(vocabulary, role);
    return {
      test: {
        t: "compare",
        op: "gte",
        left: { k: "height", of: zones.includes("setup") ? "setup" : zones[0] || "setup" },
        right: { k: "dist", unit: "atr", amount: 0.5 },
      },
    };
  }
  const form = strategyRdDefaultPriceForm(vocabulary, role);
  return { test: { t: "price", op: "closes_past", price: { of: form.of, value: "near_edge" } } };
}

// ---------------------------------------------------------------------------
// Sentence → JSON
// ---------------------------------------------------------------------------

function refJson(of: string, value: string): StrategyRdRuleExpr {
  return { ref: { of, value } };
}

export function strategyRdPriceFormToJson(form: StrategyRdPriceForm): StrategyRdRuleExpr {
  const base =
    form.value === "zone" && form.levelPct != null
      ? { level_in: [refJson(form.of, "zone"), form.levelPct] }
      : refJson(form.of, form.value);
  if (!form.offset) return base;
  return { [form.offset.dir]: [base, { [form.offset.unit]: form.offset.amount }] };
}

export function strategyRdPriceSentenceToJson(sentence: StrategyRdPriceSentence): StrategyRdRuleExpr {
  const parts = sentence.parts.map(strategyRdPriceFormToJson);
  if (sentence.combine && parts.length > 1) return { [sentence.combine]: parts };
  return parts[0] ?? {};
}

function operandToJson(o: StrategyRdRuleOperand): StrategyRdRuleExpr {
  if (o.k === "height") return { height: refJson(o.of, "zone") };
  if (o.k === "span") return { span: [refJson(o.a.of, o.a.value), refJson(o.b.of, o.b.value)] };
  if (o.k === "dist") return { [o.unit]: o.amount };
  return strategyRdPriceFormToJson(o.form);
}

function testToJson(t: StrategyRdCondTest): StrategyRdRuleExpr {
  if (t.t === "price") return { [t.op]: strategyRdPriceFormToJson(t.price) };
  if (t.t === "time") return { time_between: [t.from, t.to, t.tz] };
  if (t.t === "compare") return { [t.op]: [operandToJson(t.left), operandToJson(t.right)] };
  if (t.t === "overlaps") return { overlaps: [refJson(t.a, "zone"), refJson(t.b, "zone")] };
  return { inside: [strategyRdPriceFormToJson(t.price), refJson(t.zone, "zone")] };
}

export function strategyRdCondItemToJson(item: StrategyRdCondItem): StrategyRdRuleExpr {
  let out = testToJson(item.test);
  if (item.repeat) out = { [item.repeat.kind]: [out, item.repeat.n] };
  if (item.negate) out = { not: out };
  return out;
}

export function strategyRdCondSentenceToJson(sentence: StrategyRdCondSentence): StrategyRdRuleExpr {
  const items = sentence.items.map(strategyRdCondItemToJson);
  if (items.length === 1) return items[0];
  return { [sentence.join]: items };
}

// ---------------------------------------------------------------------------
// JSON → sentence (null = the builder can't show it; edit as JSON)
// ---------------------------------------------------------------------------

function asObj(n: unknown): Record<string, unknown> | null {
  return n && typeof n === "object" && !Array.isArray(n) ? (n as Record<string, unknown>) : null;
}

/** The node's single operator and its argument, or null. */
function single(n: unknown): [string, unknown] | null {
  const o = asObj(n);
  if (!o) return null;
  const keys = Object.keys(o);
  return keys.length === 1 ? [keys[0], o[keys[0]]] : null;
}

function parseRef(n: unknown): { of: string; value: string } | null {
  const s = single(n);
  if (!s || s[0] !== "ref") return null;
  const r = asObj(s[1]);
  const of = typeof r?.of === "string" ? r.of : "";
  const value = typeof r?.value === "string" ? r.value : "";
  return of && value ? { of, value } : null;
}

function parseDist(n: unknown): { unit: StrategyRdRuleDistanceUnit; amount: number } | null {
  const s = single(n);
  if (!s || !STRATEGY_RD_RULE_DIST_UNITS.includes(s[0] as StrategyRdRuleDistanceUnit)) return null;
  return typeof s[1] === "number" && Number.isFinite(s[1])
    ? { unit: s[0] as StrategyRdRuleDistanceUnit, amount: s[1] }
    : null;
}

function parseBase(n: unknown): StrategyRdPriceForm | null {
  const ref = parseRef(n);
  if (ref) return { of: ref.of, value: ref.value };
  const s = single(n);
  if (s?.[0] === "level_in" && Array.isArray(s[1]) && s[1].length === 2) {
    const zone = parseRef(s[1][0]);
    const pct = s[1][1];
    if (zone?.value === "zone" && typeof pct === "number") {
      return { of: zone.of, value: "zone", levelPct: pct };
    }
  }
  return null;
}

export function strategyRdPriceFormFromJson(n: unknown): StrategyRdPriceForm | null {
  const base = parseBase(n);
  if (base) return base;
  const s = single(n);
  if (!s || !OFFSET_DIRS.includes(s[0] as StrategyRdRuleOffsetDir)) return null;
  if (!Array.isArray(s[1]) || s[1].length !== 2) return null;
  const inner = parseBase(s[1][0]);
  const dist = parseDist(s[1][1]);
  if (!inner || !dist) return null;
  return { ...inner, offset: { dir: s[0] as StrategyRdRuleOffsetDir, ...dist } };
}

export function strategyRdPriceSentenceFromJson(n: unknown): StrategyRdPriceSentence | null {
  const form = strategyRdPriceFormFromJson(n);
  if (form) return { parts: [form] };
  const s = single(n);
  if (!s || !COMBINE_OPS.includes(s[0] as StrategyRdRuleCombineOp)) return null;
  if (!Array.isArray(s[1]) || s[1].length !== 2) return null;
  const parts = s[1].map(strategyRdPriceFormFromJson);
  if (parts.some((p) => p == null)) return null;
  return { combine: s[0] as StrategyRdRuleCombineOp, parts: parts as StrategyRdPriceForm[] };
}

function parseOperand(n: unknown): StrategyRdRuleOperand | null {
  const dist = parseDist(n);
  if (dist) return { k: "dist", ...dist };
  const s = single(n);
  if (s?.[0] === "height") {
    const zone = parseRef(s[1]);
    return zone?.value === "zone" ? { k: "height", of: zone.of } : null;
  }
  if (s?.[0] === "span" && Array.isArray(s[1]) && s[1].length === 2) {
    const a = parseRef(s[1][0]);
    const b = parseRef(s[1][1]);
    return a && b ? { k: "span", a, b } : null;
  }
  const form = strategyRdPriceFormFromJson(n);
  return form ? { k: "price", form } : null;
}

function parseTest(n: unknown): StrategyRdCondTest | null {
  const s = single(n);
  if (!s) return null;
  const [op, arg] = s;
  if (STRATEGY_RD_PRICE_TESTS.includes(op as StrategyRdPriceTestOp)) {
    const price = strategyRdPriceFormFromJson(arg);
    return price ? { t: "price", op: op as StrategyRdPriceTestOp, price } : null;
  }
  if (!Array.isArray(arg)) return null;
  if (op === "time_between" && arg.length === 3 && arg.every((a) => typeof a === "string")) {
    return { t: "time", from: arg[0], to: arg[1], tz: arg[2] };
  }
  if (STRATEGY_RD_COMPARE_OPS.includes(op as StrategyRdCompareOp) && arg.length === 2) {
    const left = parseOperand(arg[0]);
    const right = parseOperand(arg[1]);
    return left && right ? { t: "compare", op: op as StrategyRdCompareOp, left, right } : null;
  }
  if (op === "overlaps" && arg.length === 2) {
    const a = parseRef(arg[0]);
    const b = parseRef(arg[1]);
    return a?.value === "zone" && b?.value === "zone" ? { t: "overlaps", a: a.of, b: b.of } : null;
  }
  if (op === "inside" && arg.length === 2) {
    const price = strategyRdPriceFormFromJson(arg[0]);
    const zone = parseRef(arg[1]);
    return price && zone?.value === "zone" ? { t: "inside", price, zone: zone.of } : null;
  }
  return null;
}

export function strategyRdCondItemFromJson(n: unknown): StrategyRdCondItem | null {
  let node = n;
  let negate = false;
  let repeat: StrategyRdCondItem["repeat"] = null;
  const not = single(node);
  if (not?.[0] === "not") {
    negate = true;
    node = not[1];
  }
  const rep = single(node);
  if ((rep?.[0] === "consecutive" || rep?.[0] === "count") && Array.isArray(rep[1]) && rep[1].length === 2) {
    const count = rep[1][1];
    if (typeof count !== "number" || !Number.isInteger(count)) return null;
    repeat = { kind: rep[0], n: count };
    node = rep[1][0];
  }
  const test = parseTest(node);
  if (!test) return null;
  return { ...(negate ? { negate } : {}), ...(repeat ? { repeat } : {}), test };
}

export function strategyRdCondSentenceFromJson(n: unknown): StrategyRdCondSentence | null {
  const s = single(n);
  if ((s?.[0] === "and" || s?.[0] === "or") && Array.isArray(s[1]) && s[1].length > 0) {
    const items = s[1].map(strategyRdCondItemFromJson);
    if (items.some((i) => i == null)) return null;
    return { join: s[0], items: items as StrategyRdCondItem[] };
  }
  const item = strategyRdCondItemFromJson(n);
  return item ? { join: "and", items: [item] } : null;
}

// ---------------------------------------------------------------------------
// Errors by field
// ---------------------------------------------------------------------------

/** Where a rule-related BE error is shown: by its editor, the Manage section, or the Apply bar. */
export type StrategyRdRuleFieldId =
  | "stop.rule"
  | "target.rule"
  | "entry.rule"
  | "entry.filter"
  | "manage"
  | "general";

export type StrategyRdRuleFieldErrors = Partial<Record<StrategyRdRuleFieldId, string>>;

const FIELD_IDS: readonly StrategyRdRuleFieldId[] = [
  "stop.rule",
  "target.rule",
  "entry.rule",
  "entry.filter",
  "manage",
];

function fieldForInvalidTemplate(message: string): StrategyRdRuleFieldId {
  const m = message.toLowerCase();
  if (m.includes("entry filter")) return "entry.filter";
  if (m.includes("entry rule")) return "entry.rule";
  if (/break-even|break_even|partial|trail|time stop|time_stop|lock_r|after_r/.test(m)) return "manage";
  if (m.includes("target") && m.includes("rule")) return "target.rule";
  if (m.includes("stop") && m.includes("rule")) return "stop.rule";
  return "general";
}

/**
 * Field-level messages from Apply / Save errors: 400 `strategy_rd.invalid_template`
 * (routed by its message) and 422 `strategy_rd.pine_not_translatable`
 * (`details.missing[] = {id, reason}`). Null for any other error.
 */
export function strategyRdRuleFieldErrors(err: unknown): StrategyRdRuleFieldErrors | null {
  if (!err || typeof err !== "object") return null;
  const e = err as { slug?: unknown; message?: unknown; details?: unknown };
  const slug = typeof e.slug === "string" ? e.slug : "";
  const message = typeof e.message === "string" ? e.message.trim() : "";
  if (slug === "strategy_rd.invalid_template") {
    return message ? { [fieldForInvalidTemplate(message)]: message } : null;
  }
  if (slug !== "strategy_rd.pine_not_translatable") return null;
  const details = asObj(e.details);
  const missing = Array.isArray(details?.missing) ? details!.missing : [];
  const out: StrategyRdRuleFieldErrors = {};
  for (const item of missing) {
    const row = asObj(item);
    const id = typeof row?.id === "string" ? row.id.trim() : "";
    const reason =
      (typeof row?.reason === "string" && row.reason.trim()) ||
      "TradingView can't run this part of the recipe.";
    const field: StrategyRdRuleFieldId = FIELD_IDS.includes(id as StrategyRdRuleFieldId)
      ? (id as StrategyRdRuleFieldId)
      : id.startsWith("manage")
        ? "manage"
        : "general";
    out[field] = out[field] ? `${out[field]} ${reason}` : reason;
  }
  if (Object.keys(out).length === 0) out.general = message || "TradingView can't run this recipe.";
  return out;
}

// ---------------------------------------------------------------------------
// Manage the trade (template.manage) — same bounds as the BE's ManageRule
// ---------------------------------------------------------------------------

export type StrategyRdManageErrors = Partial<
  Record<"break_even" | "trail" | "partials" | "time_stop_bars" | "exit_at", string>
>;

export function strategyRdManageErrors(manage: StrategyRdManageRule | null | undefined): StrategyRdManageErrors {
  const out: StrategyRdManageErrors = {};
  if (!manage) return out;
  const be = manage.break_even;
  if (be) {
    if (!(be.after_r > 0) || be.after_r > 20) out.break_even = "Move after 0–20 R.";
    else if (be.lock_r < -1 || be.lock_r > 10) out.break_even = "Lock between −1 and 10 R.";
    else if (be.lock_r >= be.after_r) out.break_even = "The locked R must be below the trigger R.";
  }
  const trail = manage.trail;
  if (trail) {
    if (!(trail.mult >= 0) || trail.mult > 20) out.trail = "The multiple is 0–20.";
    else if (trail.after_r != null && (trail.after_r < 0 || trail.after_r > 20)) {
      out.trail = "Start after 0–20 R, or leave it blank to trail at once.";
    }
  }
  const partials = manage.partials ?? [];
  if (partials.length > 3) out.partials = "At most 3 partial targets.";
  else if (partials.some((p) => !(p.at_r > 0) || p.at_r > 50 || !(p.close_pct > 0) || p.close_pct >= 100)) {
    out.partials = "Each partial closes 1–99 % at 0–50 R.";
  } else if (partials.some((p, i) => i > 0 && p.at_r <= partials[i - 1].at_r)) {
    out.partials = "Partials must be at increasing R.";
  } else if (partials.reduce((s, p) => s + p.close_pct, 0) > 95) {
    out.partials = "Partials close at most 95 % in total. The rest rides to the target or stop.";
  }
  const bars = manage.time_stop_bars;
  if (bars != null && (!Number.isInteger(bars) || bars < 1)) out.time_stop_bars = "Whole bars, 1 or more.";
  return out;
}

/** `template.manage` for the wire: empty parts dropped, null when nothing is on. */
export function strategyRdManageForWire(
  manage: StrategyRdManageRule | null | undefined
): StrategyRdManageRule | null {
  if (!manage) return null;
  // Settings this editor doesn't show (exit at a time, oscillator / MA exits TES set) ride along.
  const out: StrategyRdManageRule = {};
  for (const [k, v] of Object.entries(manage)) {
    if (v != null && !["break_even", "trail", "partials", "time_stop_bars"].includes(k)) {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  if (manage.break_even) out.break_even = manage.break_even;
  if (manage.trail) {
    const { after_r, ...rest } = manage.trail;
    out.trail = after_r != null ? { ...rest, after_r } : rest;
  }
  if (manage.partials && manage.partials.length > 0) out.partials = manage.partials;
  if (manage.time_stop_bars != null) out.time_stop_bars = manage.time_stop_bars;
  return Object.keys(out).length > 0 ? out : null;
}

// ---------------------------------------------------------------------------
// Template helpers
// ---------------------------------------------------------------------------

/** The rule a role holds in the template (entry: the whole `{order, price, when, cancel_if}`). */
export function strategyRdTemplateRule(
  template: {
    entry: { rule?: unknown; filter?: unknown };
    stop: { rule?: unknown };
    target: { rule?: unknown };
  },
  role: StrategyRdRuleRole
): unknown {
  if (role === "entry") return template.entry.rule ?? null;
  if (role === "filter") return template.entry.filter ?? null;
  return template[role].rule ?? null;
}

/**
 * Merge a compile `template_patch` (`{stop: {rule}}`, `{entry: {mode, rule}}`,
 * `{entry: {filter}}`) into the template, one level deep per section.
 */
export function strategyRdMergeTemplatePatch<T extends object>(
  template: T,
  patch: Record<string, unknown> | null | undefined
): T {
  if (!patch) return template;
  const out: Record<string, unknown> = { ...(template as Record<string, unknown>) };
  for (const [section, value] of Object.entries(patch)) {
    const cur = out[section];
    out[section] =
      asObj(value) && asObj(cur) ? { ...(cur as object), ...(value as object) } : value;
  }
  return out as T;
}

/**
 * TES `template_patch` / `sizing_patch` (§10): deep-merge into the current value; a `null`
 * clears that field (e.g. `stop.rule: null` returns the stop to its preset). Arrays replace.
 */
export function strategyRdDeepMergePatch<T extends object>(
  base: T,
  patch: Record<string, unknown> | null | undefined
): T {
  if (!patch) return base;
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) {
      delete out[key];
      continue;
    }
    const cur = out[key];
    out[key] =
      asObj(value) && asObj(cur)
        ? strategyRdDeepMergePatch(cur as Record<string, unknown>, value as Record<string, unknown>)
        : value;
  }
  return out as T;
}

/**
 * TES Confirm (§10): prefer full merged `template` replace; else deep-merge `template_patch`.
 * Callers must not invent `entry.mode: "rule"` from composer alone.
 */
export function strategyRdConfirmCoachTemplate(
  pageTemplate: StrategyRdRuleTemplate,
  proposal: {
    template?: Record<string, unknown> | null;
    template_patch?: Record<string, unknown> | null;
  },
  parseTemplate: (raw: unknown) => StrategyRdRuleTemplate
): StrategyRdRuleTemplate {
  if (proposal.template) return parseTemplate(proposal.template);
  if (proposal.template_patch) {
    return strategyRdDeepMergePatch(pageTemplate, proposal.template_patch);
  }
  return pageTemplate;
}

/**
 * Composer → template entry sync. Never writes bare `{mode:"rule"}` — custom Entry
 * bodies only arrive via `turn.template` / `template_patch`.
 */
export function strategyRdTemplateEntryFromComposerMode(
  prevEntry: StrategyRdRuleTemplate["entry"],
  composerMode: string | null | undefined,
  sidePolicy?: StrategyRdSidePolicy | string | null
): StrategyRdRuleTemplate["entry"] {
  const mode = (composerMode || "").trim();
  const withPolicy = {
    ...prevEntry,
    ...(sidePolicy ? { side_policy: sidePolicy } : {}),
  };
  if (mode === "rule") return withPolicy;
  const next: StrategyRdRuleTemplate["entry"] = {
    ...withPolicy,
    mode: (mode || prevEntry.mode) as StrategyRdEntryMode,
  };
  if (prevEntry.mode === "rule" && mode && mode !== "rule") {
    const { rule: _drop, ...rest } = next;
    return rest;
  }
  return next;
}
